/**
 * CustomsFormController
 *
 * Wraps a Shopify Forms app embed (<shopify-forms-embed>, shadow DOM) inside the Customs
 * section and lets other code patch field styles, surviving
 * the widget's own re-renders (it replaces its shadow content on every step/validation change).
 *
 * Usage (in sections/customs.liquid):
 *   <customs-form-controller>
 *     {% content_for 'blocks' %}
 *   </customs-form-controller>
 *
 * Usage (anywhere after the element upgrades):
 *   const controller = document.querySelector('customs-form-controller');
 *   controller.setFieldStyle('[name="custom#signature"]', 'color: red;');
 *   controller.setFieldStyle('[name="custom#signature"] span', 'font-weight: 700;');
 */

const CUSTOMS_CLASSES = {
  inner: 'customs__inner',
  text: 'customs__text',
  hidden: 'is-hidden',
  loading: 'is-loading',
};

const CUSTOMS_SELECTORS = {
  formEmbed: 'shopify-forms-embed',
  heading: '#app-embed h2',
  policy: 'form + div',
  legend: 'fieldset[name="custom#signature"] legend',
  label: 'fieldset[name="custom#signature"] label',
  input: 'fieldset[name="custom#signature"] input',
  success: 'div[data-testid="app-router"] section[aria-label="Success"]',
};

const STYLE_ID = 'customs-form-controller-style';

/** Milliseconds to keep polling for the widget's shadow root before giving up. */
const POLL_TIMEOUT = 30000;
const POLL_INTERVAL = 200;

class CustomsFormController extends HTMLElement {
  /** @type {{ selector: string, declarations: string }[]} */
  _cssRules = [];

  /** @type {ShadowRoot | null} */
  _shadowRoot = null;

  /** @type {MutationObserver | null} */
  _observer = null;

  /** @type {number | null} */
  _pollTimer = null;

  connectedCallback() {
    this._setLoading(true);
    this._pollForEmbed();
    this.setFieldStyle(CUSTOMS_SELECTORS.heading, 'line-height: var(--font-h2--line-height);');
    this.setFieldStyle(CUSTOMS_SELECTORS.legend, 'display: none;');
    this.setFieldStyle(CUSTOMS_SELECTORS.policy, 'display: none;');
    this.setFieldStyle(CUSTOMS_SELECTORS.label, 'align-items: flex-start;');
    this.setFieldStyle(CUSTOMS_SELECTORS.input, 'height: 20px;');
    this.setFieldStyle(CUSTOMS_SELECTORS.success, 'display: flex; align-items: center; justify-content: center;');
  }

  disconnectedCallback() {
    this._stopPolling();

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    this._shadowRoot = null;
  }

  /**
   * Sets (or clears) arbitrary CSS declarations for elements matched by a full CSS selector.
   * Calling it again for the same selector replaces the previous declarations.
   * @param {string} selector - a CSS selector inside the form, e.g.
   *   '[name="custom#signature"] span'
   * @param {string} declarations - raw CSS declarations, e.g. "color: red; font-weight: 700;"
   * @returns {void}
   */
  setFieldStyle(selector, declarations) {
    const normalizedSelector = this._buildSelector(selector);
    this._cssRules = this._cssRules.filter((rule) => rule.selector !== normalizedSelector);

    if (declarations) {
      this._cssRules.push({ selector: normalizedSelector, declarations });
    }

    this._applyStyle();
  }

  /**
   * Normalizes selectors shared by style rules and content replacements.
   * @param {string} selector
   * @returns {string}
   */
  _buildSelector(selector) {
    return selector.trim();
  }

  /**
   * Polls for the Shopify Forms embed to upgrade and expose its shadow root. The app can take
   * a moment to mount, and this element may connect before that happens.
   * @returns {void}
   */
  _pollForEmbed() {
    this._stopPolling();

    const tick = () => {
      const embed = this.querySelector(CUSTOMS_SELECTORS.formEmbed);
      if (!(embed instanceof HTMLElement) || !embed.shadowRoot) return;

      this._stopPolling();
      this._setLoading(false);
      this._observeEmbed(embed.shadowRoot);
    };

    tick();
    if (this._shadowRoot) return;

    const deadline = Date.now() + POLL_TIMEOUT;
    this._pollTimer = window.setInterval(() => {
      tick();
      if (Date.now() > deadline) this._stopPolling();
    }, POLL_INTERVAL);
  }

  /**
   * @returns {void}
   */
  _stopPolling() {
    if (this._pollTimer == null) return;
    window.clearInterval(this._pollTimer);
    this._pollTimer = null;
  }

  /**
   * @param {boolean} isLoading
   * @returns {void}
   */
  _setLoading(isLoading) {
    const container = this.closest(`.${CUSTOMS_CLASSES.inner}`);
    if (!container) return;

    container.classList.toggle(CUSTOMS_CLASSES.loading, isLoading);
    container.setAttribute('aria-busy', String(isLoading));
  }

  /**
   * @param {ShadowRoot} shadowRoot
   * @returns {void}
   */
  _observeEmbed(shadowRoot) {
    this._shadowRoot = shadowRoot;
    this._applyStyle();
    this._syncSignatureText();

    if (this._observer) this._observer.disconnect();

    this._observer = new MutationObserver(() => {
      this._applyStyle();
      this._syncSignatureText();
    });

    this._observer.observe(shadowRoot, {
      attributes: true,
      attributeFilter: ['data-current-step'],
      childList: true,
      subtree: true,
    });
  }

  /**
   * Hides the signature text after the form has been submitted successfully.
   * @returns {void}
   */
  _syncSignatureText() {
    const shadowRoot = this._shadowRoot;
    const signatureText = this.querySelector(`.${CUSTOMS_CLASSES.text}`);
    if (!shadowRoot || !signatureText) return;

    const appEmbed = shadowRoot.querySelector('#app-embed');
    signatureText.classList.toggle(
      CUSTOMS_CLASSES.hidden,
      appEmbed?.getAttribute('data-current-step') === 'success'
    );
  }

  /**
   * Idempotent: re-running with the same rules is a no-op, so the MutationObserver above
   * cannot loop on its own writes.
   * @returns {void}
   */
  _applyStyle() {
    const shadowRoot = this._shadowRoot;
    if (!shadowRoot) return;

    const css = this._cssRules.map(({ selector, declarations }) => `${selector}{${declarations}}`).join('');

    let style = shadowRoot.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      shadowRoot.appendChild(style);
    }

    if (style.textContent !== css) style.textContent = css;
  }
}

if (!customElements.get('customs-form-controller')) {
  customElements.define('customs-form-controller', CustomsFormController);
}
